<h3 mat-dialog-title>
  @if (readOnly) {
    Anomalie archivée —
  }
  {{ anomaly?.title ?? 'Nouvelle anomalie' }}
</h3>
<mat-dialog-content>
  <form [formGroup]="form" class="anomaly-form">
    <div class="title-row">
      <mat-form-field class="title">
        <mat-label>Titre</mat-label>
        <input matInput formControlName="title" required />
        <mat-hint>La ligne qu’on lit quand le bandeau est réduit</mat-hint>
      </mat-form-field>
      <mat-button-toggle-group
        formControlName="status"
        aria-label="Statut de l’anomalie"
        class="status"
        [class.resolved]="form.controls.status.value === 'RESOLVED'"
      >
        <mat-button-toggle value="IN_PROGRESS">
          <svg lucideTriangleAlert></svg>
          En cours
        </mat-button-toggle>
        <mat-button-toggle value="RESOLVED">
          <svg lucideCircleCheck></svg>
          Résolue
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    <mat-tab-group class="content-tabs" animationDuration="0ms" mat-stretch-tabs="false">
      <mat-tab label="Écrire">
        @if (!readOnly) {
          <div class="toolbar" role="toolbar" aria-label="Mise en forme">
            <button
              mat-icon-button
              type="button"
              aria-label="Gras"
              matTooltip="Gras"
              (click)="format('bold')"
            >
              <svg lucideBold></svg>
            </button>
            <button
              mat-icon-button
              type="button"
              aria-label="Italique"
              matTooltip="Italique"
              (click)="format('italic')"
            >
              <svg lucideItalic></svg>
            </button>
            <button
              mat-icon-button
              type="button"
              aria-label="Liste"
              matTooltip="Liste"
              (click)="format('list')"
            >
              <svg lucideList></svg>
            </button>
            <button
              mat-icon-button
              type="button"
              aria-label="Lien"
              matTooltip="Lien"
              (click)="format('link')"
            >
              <svg lucideLink></svg>
            </button>
          </div>
        }
        <mat-form-field class="content">
          <mat-label>Contenu</mat-label>
          <textarea #contentArea matInput formControlName="content" rows="7" required></textarea>
          <mat-hint
            >Markdown : **gras**, *italique*, listes, liens. Le HTML n’est pas interprété.</mat-hint
          >
        </mat-form-field>
      </mat-tab>
      <mat-tab label="Aperçu">
        <app-anomaly-banner
          class="preview"
          [anomaly]="preview()"
          [actions]="false"
        ></app-anomaly-banner>
      </mat-tab>
    </mat-tab-group>

    <mat-form-field class="roles">
      <mat-label>Rôles cibles</mat-label>
      <mat-select
        formControlName="targetRoles"
        multiple
        required
        (selectionChange)="onRolesChange($event.value)"
      >
        <mat-select-trigger>{{ rolesTrigger() }}</mat-select-trigger>
        <mat-option [value]="ALL_ROLES" class="all-roles">Tous les rôles</mat-option>
        @for (role of roles; track role) {
          <mat-option [value]="role">{{ roleLabel(role) }}</mat-option>
        }
      </mat-select>
      <mat-hint>{{ rolesHint() }}. « Tous les rôles » en tête de liste les coche tous.</mat-hint>
      <mat-error>Choisissez au moins un rôle cible</mat-error>
    </mat-form-field>

    <div class="period">
      <div class="bound">
        <mat-form-field class="date">
          <mat-label>Début</mat-label>
          <input matInput formControlName="startDate" [matDatepicker]="startPicker" />
          <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
          <mat-datepicker #startPicker></mat-datepicker>
          <mat-hint>Vide : affichée dès l’enregistrement</mat-hint>
        </mat-form-field>
        <mat-form-field class="time">
          <mat-label>Heure</mat-label>
          <input matInput type="time" step="60" formControlName="startTime" />
        </mat-form-field>
      </div>
      <div class="bound">
        <mat-form-field class="date">
          <mat-label>Fin</mat-label>
          <input matInput formControlName="endDate" [matDatepicker]="endPicker" />
          <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
          <mat-datepicker #endPicker></mat-datepicker>
          <mat-hint>Vide : tant qu’elle est en cours, puis 2 jours</mat-hint>
        </mat-form-field>
        <mat-form-field class="time">
          <mat-label>Heure</mat-label>
          <input matInput type="time" step="60" formControlName="endTime" />
        </mat-form-field>
      </div>
    </div>
    @if (form.hasError('endBeforeStart')) {
      <mat-error>La fin doit suivre le début.</mat-error>
    }
    @if (form.hasError('timeWithoutDate')) {
      <mat-error>Une heure se saisit avec sa date.</mat-error>
    }

    <p class="audience" [class.resolved]="form.controls.status.value === 'RESOLVED'">
      {{ audience() }}
    </p>

    @if (anomaly) {
      <p class="traceability">
        Signalée par {{ anomaly.createdBy | userIdToUserName }} le
        {{ formatDateTime(anomaly.createdAt) }}.
        @if (anomaly.updatedBy && anomaly.updatedAt) {
          Modifiée par {{ anomaly.updatedBy | userIdToUserName }} le
          {{ formatDateTime(anomaly.updatedAt) }}.
        }
        @if (anomaly.resolvedBy && anomaly.resolvedAt) {
          Résolue par {{ anomaly.resolvedBy | userIdToUserName }} le
          {{ formatDateTime(anomaly.resolvedAt) }}.
        }
        @if (anomaly.archivedBy && anomaly.archivedAt) {
          Archivée par {{ anomaly.archivedBy | userIdToUserName }} le
          {{ formatDateTime(anomaly.archivedAt) }}.
        }
      </p>
    }
    @if (error()) {
      <p class="api-error" role="alert">{{ error() }}</p>
    }
  </form>
</mat-dialog-content>
<mat-dialog-actions>
  @if (!readOnly) {
    <span class="delay-note"
      ><svg lucideClock></svg>Visible pour les rôles ciblés dans la minute</span
    >
  }
  @if (anomaly && !readOnly) {
    <button matButton type="button" (click)="onArchive()">
      {{ confirmArchive() ? "Confirmer l'archivage" : 'Archiver' }}
    </button>
  }
  <button matButton type="button" (click)="onCancel()">
    {{ readOnly ? 'Fermer' : 'Annuler' }}
  </button>
  @if (!readOnly) {
    <app-save-button
      (click)="onSave()"
      [isEditMode]="!!anomaly"
      [enabled]="canSave()"
    ></app-save-button>
  }
</mat-dialog-actions>
